import { AfterViewInit, Component, NgZone } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { AuthProvider } from '@app/interfaces/sys/auth';
import { StartupService } from '@core';
import { environment } from '@env/environment';
import { AuthHttp } from '@services/sys/auth.service';
import { TokenService } from '@services/sys/token.service';
import dayjs from 'dayjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subscription } from 'rxjs';

declare var google: any;

@Component({
    selector: 'app-user-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.less'],
})
export class UserLoginComponent implements AfterViewInit {
    autoTips: Record<string, Record<string, string>> = {
        default: {
            required: 'Input is required',
            email: 'The input is not valid email',
        },
    };
    loading = false;
    beforeName: string | null = '';
    private googleButtonSubscription!: Subscription;

    constructor(
        fb: FormBuilder,
        private router: Router,
        private startupSrv: StartupService,
        public service: AuthHttp,
        public msg: NzMessageService,
        private tokenService: TokenService,
        private route: ActivatedRoute,
        private ngZone: NgZone
    ) {
        const username = localStorage.getItem('username');
        console.log('username', username);
        this.form = fb.group({
            userName: [username, [Validators.required]],
            password: [null, [Validators.required]],
            remember: [true],
        });
        this.googleButtonSubscription = this.service.googleButtonObservable$.subscribe(() => {
            this.initializeGoogleButton();
        });
    }


    ngAfterViewInit() {
        this.initializeGoogleButton();
    }

    initializeGoogleButton() {
        const renderGoogleButton = () => {
            this.ngZone.runOutsideAngular(() => {
                google.accounts.id.initialize({
                    client_id: environment.auth.google.clientId,
                    use_fedcm_for_prompt: true,
                    callback: (resp: any) => {
                        this.handleLogin(resp);
                    }
                });

                google.accounts.id.renderButton(document.getElementById("google-btn"),
                    {
                        theme: "filled_blue", 
                        size: "large",
                        shape: "rectangle",
                        width: 310,
                    });
                console.log('Google button initialized');
            });
        };

        if (typeof google !== 'undefined') {
            renderGoogleButton();
        } else {
            console.log('Google not defined, setting up script load listener');
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = renderGoogleButton;
            document.head.appendChild(script);
        }
    }
    


    async handleLogin(resp: any) {
        localStorage.removeItem('hasCheckLicence');
        sessionStorage.removeItem('hasExpired');
        localStorage.setItem('firstLogin', 'true');
        // this.error = '';
        // this.userName.markAsDirty();
        // this.userName.updateValueAndValidity();
        // this.password.markAsDirty();
        // this.password.updateValueAndValidity();
        try {
            this.loading = true;
            const data = await this.service.loginByExternalAuthProvider({
                provider: AuthProvider.GOOGLE,
                jwt: resp.credential
            });
            this.beforeName = localStorage.getItem('username');
            if (this.remember.value) {
                localStorage.setItem('username', this.userName.value);
            } else {
                localStorage.removeItem('username');
            }
            await this.tokenService.set(data);
            this.route.queryParams.subscribe(async (queryData) => {
                const redirectLink = queryData.redirect;
                const { firstLink } = await this.startupSrv.load();
                const toLink = redirectLink ? redirectLink : firstLink;
                this.router.navigateByUrl(toLink || '/');
            });
        } catch (e) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    get versionInfo(): any {
        return {
            version: environment.version,
            commit: environment.commit,
        };
    }

    // #region fields

    get userName(): AbstractControl {
        return this.form.controls.userName;
    }

    get password(): AbstractControl {
        return this.form.controls.password;
    }

    get remember(): AbstractControl {
        return this.form.controls.remember;
    }

    get currentYear(): string {
        return dayjs().format('YYYY');
    }

    form: FormGroup;
    error = '';

    async submit(): Promise<void> {
        this.error = '';
        this.userName.markAsDirty();
        this.userName.updateValueAndValidity();
        this.password.markAsDirty();
        this.password.updateValueAndValidity();
        if (this.userName.invalid || this.password.invalid) {
            return;
        }

        try {
            this.loading = true;
            const data = await this.service.login({
                username: this.userName.value,
                password: this.password.value,
            });
            if (this.remember.value) {
                localStorage.setItem('username', this.userName.value);
            } else {
                localStorage.removeItem('username');
            }

            this.tokenService.set(data);
            this.route.queryParams.subscribe(async (queryData) => {
                const redirectLink = queryData.redirect;
                const { firstLink } = await this.startupSrv.load();
                const toLink = redirectLink ? redirectLink : firstLink;
                this.router.navigateByUrl(toLink || '/');
            });
        } catch (e) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    ngOnDestroy() {
        if (this.googleButtonSubscription) {
            this.googleButtonSubscription.unsubscribe();
        }
    }
}
